import { _ } from "../../../polyglot/polyglot.js";
import { currentPeriodType } from "../../../../models/period/PeriodType.js";
import IndexPicker from "../../../../accounting/shared/IndexPicker/IndexPicker.js";
import PeriodTypePicker from "../../../../accounting/shared/PeriodTypePicker/PeriodTypePicker.js";
import regionWidget from "../../regionWidget.js";

/**
 * Make a period section renderer.
 *
 * @param {object} options - The options object
 * @param {boolean} options.isBimesterEnabled - Whether bimester is enabled
 * @param {PeriodType} options.periodType - The period type
 * @param {function} [options.shouldShowFn] - The function to determine if the
 *   section should show
 * @param {number} [options.shift] - The shift value
 * @param {PeriodType} [options.shiftPeriodType] - The shift period type
 * @param {function} options.updateNodeAttributes - The function to update the
 *   node attributes
 * @param {PeriodType} [options.defaultPeriodType] - The default period type
 * @param {boolean} [options.isYearToDateEnabled] - Whether year to date is
 *   enabled
 * @param {boolean} [options.canShift] - Whether the period can be shifted
 * @return {Renderable}
 */
export function periodSectionRenderer({
	isBimesterEnabled,
	periodType,
	shouldShowFn = () => true,
	shift,
	shiftPeriodType,
	updateNodeAttributes,
	defaultPeriodType = currentPeriodType,
	isYearToDateEnabled = false,
	canShift = true,
	onChange = () => {},
}) {
	const shiftPeriodPicker = new IndexPicker({
		index: shift || 0,
		callback: (index) => {
			updateNodeAttributes({ shift: index });
		},
	});

	const shiftPeriodTypeDropdown = instantiatePeriodTypePicker({
		isBimesterEnabled,
		isYearToDateEnabled,
		spec: {
			minimumPeriodType: periodType ?? defaultPeriodType,
			selectedPeriodTypeId:
				shiftPeriodType?.getId() || defaultPeriodType.getId(),
			onInitialize: (selectedPeriodType) =>
				updateNodeAttributes({
					shiftPeriodType: selectedPeriodType.getId(),
				}),
			onChange: (selectedPeriodType) => {
				updateNodeAttributes({
					shiftPeriodType: selectedPeriodType.getId(),
				});

				onChange();
			},
		},
	});

	const periodTypeDropdown = instantiatePeriodTypePicker({
		isBimesterEnabled,
		isYearToDateEnabled,
		spec: {
			selectedPeriodTypeId:
				periodType?.getId() || defaultPeriodType.getId(),
			onInitialize: (selectedPeriodType) =>
				updateNodeAttributes({
					periodType: selectedPeriodType.getId(),
				}),
			onChange: (selectedPeriodType) => {
				const siftPeriodType =
					shiftPeriodTypeDropdown.setMinimumPeriodTypeSilently(
						selectedPeriodType,
					);

				updateNodeAttributes({
					periodType: selectedPeriodType.getId(),
					shiftPeriodType: siftPeriodType.getId(),
				});

				onChange();
			},
		},
	});

	return regionWidget({
		content: (html) => {
			const shouldShow = shouldShowFn();

			html.div(
				{
					class: [
						"period-type",
						"popover-section",
						{ disabled: !shouldShow },
					],
				},
				html.div(
					{ klass: "right-menu-block", "data-test": "period-type" },
					html.span({ klass: "switch-label" }, _("Period type")),
					periodTypeDropdown,
				),
			);

			html.div(
				{
					class: [
						"period-shift",
						"popover-section",
						{ disabled: !shouldShow },
					],
				},
				html.div(
					{ klass: "right-menu-block", "data-test": "shift-period" },
					html.span({ klass: "switch-label" }, _("Shift period")),
					canShift
						? html.div(
								{ klass: "flex-container" },
								shiftPeriodPicker,
								shiftPeriodTypeDropdown,
							)
						: html.div(
								{ klass: "alert alert-warning" },
								_(
									"This setting is not available for the selected period",
								),
							),
				),
			);
		},
	});
}

function instantiatePeriodTypePicker({
	isBimesterEnabled,
	isYearToDateEnabled,
	spec,
}) {
	return PeriodTypePicker.forCommonTypes({
		isBimesterEnabled,
		isYearToDateEnabled,
		spec,
	});
}
